import React, { useEffect, useState } from "react";
import "./AeContacts.css";

const Icon = ({ children }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {children}
  </svg>
);
const CopyIcon = () => <Icon><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" /></Icon>;
const CheckIcon = () => <Icon><path d="m5 12 5 5 9-10" /></Icon>;

export const contactName = (c) => [c.first_name, c.last_name].map((s) => (s || "").trim()).filter(Boolean).join(" ");

// "AB" from the first and last name; "AE" when neither has a letter.
export function initials(c) {
  const letter = (s) => {
    const m = (s || "").trim().match(/[A-Za-z]/);
    return m ? m[0].toUpperCase() : "";
  };
  return (letter(c.first_name) + letter(c.last_name)) || "AE";
}

// tel: keeps the digits and a leading +.
export const telHref = (phone) => `tel:${(phone || "").replace(/[^\d+]/g, "")}`;

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  if (!navigator.clipboard || !navigator.clipboard.writeText) return null;
  return (
    <button
      type="button"
      className="ppe-ae-copy"
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      title={copied ? "Copied" : `Copy ${label}`}
      onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true), () => {})}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}

function AeContact({ contact }) {
  const name = contactName(contact);
  return (
    <div className="ppe-ae-contact">
      <span className="ppe-ae-avatar" aria-hidden="true">{initials(contact)}</span>
      <div className="ppe-ae-body">
        <div className="ppe-ae-name">
          <b>{name}</b>
          {contact.role ? <span className="ppe-muted"> · {contact.role}</span> : null}
        </div>
        {(contact.phone || contact.email) && (
          <div className="ppe-ae-reach">
            {contact.phone && (
              <span className="ppe-ae-item">
                <a href={telHref(contact.phone)}>{contact.phone}</a>
                <CopyButton text={contact.phone} label={`${name}'s phone`} />
              </span>
            )}
            {contact.email && (
              <span className="ppe-ae-item">
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
                <CopyButton text={contact.email} label={`${name}'s email`} />
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// The lender's Account Executive at the top of a rate's detail: the first
// one, the others behind "+N more AE". Nothing when the lender has none.
export default function AeContacts({ contacts, lenderName }) {
  const [expanded, setExpanded] = useState(false);
  if (!contacts || contacts.length === 0) return null;
  const [first, ...rest] = contacts;
  return (
    <section className="ppe-ae" aria-label={lenderName ? `${lenderName} Account Executive` : "Account Executive"}>
      <AeContact contact={first} />
      {expanded && rest.map((c, i) => <AeContact contact={c} key={i} />)}
      {rest.length > 0 && (
        <button type="button" className="ppe-link ppe-ae-more" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
          {expanded ? "Show less" : `+${rest.length} more AE`}
        </button>
      )}
    </section>
  );
}
